// Import components
import React, { useState } from "react";
import { Card, CardBody, Row, Col, NavItem, Nav, NavLink, Alert, Modal, ModalHeader, Button } from 'reactstrap';
import axios from "axios";
import { useEffect } from "react";
import { ActionMenu } from "Components/Common/ActionMenu";
import classnames from "classnames";
import {Comment, CommentsBox} from "Components/Common/CommentsBox";

interface column {
    header: string;
    accessorKey?: string;
    accessorFn?: (row: any) => string;
    enableColumnFilter: boolean;
    enableSorting: boolean;
    cell?: (info: any) => React.ReactNode;
}

interface Task {
    id: number;
    name: string;
    description: string;
    status: string;
}

interface PETaskManagementProps {
    tasksData: Task[];
    assignmentId: string;
    onTasksUpdate?: () => void;
}

const PETaskManagement: React.FC<PETaskManagementProps> = ({ tasksData, assignmentId, onTasksUpdate }) => {
    const [taskId, setTaskId] = useState<number>(tasksData && tasksData.length > 0 ? tasksData[0].id : 0);

    const [showloader, setshowloader] = useState(true);
    const [alertMsgg, setAlertMsgg] = useState<any>({ open: false, type: "success", content: "Messages here.", time: "2500" });

    const userRole = localStorage.getItem('userole_');
    const canEdit = ['adm', 'prt', 'emp'].includes(userRole || '');
    const [editMode, setEditMode] = useState(false);
    const [addTaskModal, setAddTaskModal] = useState(false);
    const [deleteTaskModal, setDeleteTaskModal] = useState(false);
    const [addStepModal, setAddStepModal] = useState(false);
    const [deleteStepModal, setDeleteStepModal] = useState(false);

    const [assignmentData, setAssignmentData] = useState<Record<string, any>>({});
    const [clientData, setClientData] = useState<Record<string, any>>({});
    const [taskData, setTaskData] = useState<Record<string, any>>({});
    const [stepsData, setStepsData] = useState<Record<string, any>>([]);
    const [commentsData, setCommentsData] = useState<Record<string, any>>([]);
    const [filesData, setFilesData] = useState<Record<string, any>>([]);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    const [taskDetails, setTaskDetails] = useState<any>({
        name: "",
        description: "",
        status: "pending",
        assignment: ""
    });

    const [stepDetails, setStepDetails] = useState<any>({
        title: "",
        description: "",
        status: "pending",
        task: 0,
        can_assignee_view: true,
        can_client_view: true,
        order: 0
    });

    const handleAlertMsggClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === "clickaway") { return; }
        setAlertMsgg({ ...alertMsgg, open: false });
    };

    useEffect(() => {
        if (tasksData && tasksData.length > 0) {
            setTaskId(tasksData[0].id);
        }
    }, [tasksData]);

    const fetch_task = async () => {
        try {
            setshowloader(true);
            const response = await axios.get(
                `/assignments/getTask`,
                {
                    params: { task_id: taskId }
                },
                { withCredentials: true }
            );
            const data = response || {};
            if (data && data.status === "SENT") {
                setTaskData(data.task || {});
                setAssignmentData(data.assignment || {});
                setClientData((data.assignment || {}).client || {});
                setStepsData(data.steps || []);
                setCommentsData(data.comments || []);
                setFilesData(data.files || []);
            }
            setshowloader(false);
        } catch (error: any) {
            if (error.response) {
                if ([401, 422].includes(error.response.status)) {
                    setAlertMsgg({ ...alertMsgg, open: true, content: error.response?.error || "Error occurred", type: "error" });
                } else if (error.response.status === 403) {
                    setAlertMsgg({ ...alertMsgg, open: true, content: "Session expired!", type: "error" });
                    setTimeout(() => { window.location.href = "/logout"; }, 2000);
                } else {
                    setAlertMsgg({ ...alertMsgg, open: true, content: "An error occurred", type: "error" });
                }
            } else {
                setAlertMsgg({ ...alertMsgg, open: true, content: "Network error", type: "error" });
            }
            setshowloader(false);
        }
    };


    const get_view_url = async (fileKey: string) => {
        try {
            console.log('Getting URL for fileKey:', fileKey);
            const resp = await axios.get(
                "/assignments/files/get-file-url/",
                { params: { fileKey, expiresIn: 60 }, withCredentials: true }
            );
            console.log('View URL response:', resp);
            return resp.fileUrl as string;
        } catch (e) {
            console.error('Error getting view URL:', e);
            setAlertMsgg({ ...alertMsgg, open: true, content: "Failed to get file URL.", type: "error" });
            return null;
        }
    };

    const deleteFile = async (fileKey: string) => {
        try {
            console.log('Deleting file:', fileKey);
            setshowloader(true);
            const resp = await axios.delete(
                "/assignments/files/delete/",
                { 
                    data: { fileKey, task_id: taskId }, 
                    withCredentials: true 
                }
            );
            console.log('Delete response:', resp);
            if (resp.message) {
                setAlertMsgg({ ...alertMsgg, open: true, content: "File deleted successfully.", type: "success" });
                await fetch_task();
            } else {
                setAlertMsgg({ ...alertMsgg, open: true, content: "Failed to delete file.", type: "error" });
            }
        } catch (error: any) {
            console.error('Delete error:', error);
            setAlertMsgg({ ...alertMsgg, open: true, content: error?.response?.error || "Delete error.", type: "error" });
        } finally {
            setshowloader(false);
        }
    };

    const handleUpload = async () => {
        try {
            if (!selectedFile) {
                console.log('no file selected');
                
                setAlertMsgg({ ...alertMsgg, open: true, content: "Please select a file.", type: "error" });
                return;
            }
            
            if (!assignmentId) {
                console.log('assignment not loaded');
                
                setAlertMsgg({ ...alertMsgg, open: true, content: "Assignment not loaded.", type: "error" });
                return;
            }

            if (!taskId || taskId === 0) {
                setAlertMsgg({ ...alertMsgg, open: true, content: "Please select a task first.", type: "error" });
                return;
            }

            setshowloader(true);
            const form = new FormData();
            form.append("file", selectedFile);
            form.append("assignment_id", String(assignmentId));
            form.append("task_id", String(taskId));
            const resp = await axios.post("/assignments/files/upload/", form, {
                withCredentials: true,
                headers: { "Content-Type": "multipart/form-data" },
            });
            console.log('Upload response:', resp);
            if (resp.fileUrl && resp.fileKey) {
                setAlertMsgg({ ...alertMsgg, open: true, content: "File uploaded successfully.", type: "success" });
                setSelectedFile(null);
                // Clear the file input
                const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
                if (fileInput) fileInput.value = '';
                // Refresh task data including files
                await fetch_task();
            } else {
                setAlertMsgg({ ...alertMsgg, open: true, content: "Upload failed.", type: "error" });
            }
        } catch (error: any) {
            setAlertMsgg({ ...alertMsgg, open: true, content: error?.response?.error || "Upload error.", type: "error" });
        } finally {
            setshowloader(false);
        }
    };

    const changeStepStatus = async (stepId: string, status: string) => {
        try {
            setshowloader(true);
            const response = await axios.post(
                "/assignments/changeStepStatus/",
                { step_id: stepId, status },
                { withCredentials: true }
            );
            if (response && response.status === "UPDATED") {
                setAlertMsgg({ ...alertMsgg, open: true, content: "Step status updated.", type: "success" });
                fetch_task();
            } else {
                setAlertMsgg({ ...alertMsgg, open: true, content: response?.error || "Failed to update step status.", type: "error" });
            }
        } catch (error: any) {
            setAlertMsgg({ ...alertMsgg, open: true, content: "Error updating step status.", type: "error" });
        } finally {
            setshowloader(false);
        }
    };

    const addNewComment = async (newComment: string) => {
        try {
            setshowloader(true);
            const response = await axios.post(
                "/assignments/addComment/",
                { task_id: taskId, text: newComment },
                { withCredentials: true }
            );
            if (response && response.status === "CREATED") {
                setAlertMsgg({ ...alertMsgg, open: true, content: "Comment created.", type: "success" });
                fetch_task();
            } else {
                setAlertMsgg({ ...alertMsgg, open: true, content: response?.error || "Failed to create comment.", type: "error" });
            }
        } catch (error: any) {
            setAlertMsgg({ ...alertMsgg, open: true, content: "Error creating comment.", type: "error" });
        } finally {
            setshowloader(false);
        }
    }

    function removeBodyCss() {
        document.body.classList.add("no_padding");
    }

    function toggleDeleteTaskModal() {
        setDeleteTaskModal(!deleteTaskModal);
        removeBodyCss();
    }

    const toggleAddUpdateTaskModal = () => {
        setAddTaskModal(!addTaskModal);
        removeBodyCss();
    }

    function toggleDeleteStepModal() {
        setDeleteStepModal(!deleteStepModal);
        removeBodyCss();
    }

    const toggleAddUpdateStepModal = () => {
        setAddStepModal(!addStepModal);
        removeBodyCss();
    }

    const handleTask = async (operation = "create") => {
        // For create/update, ensure required fields are present
        if (
            (operation === "create" || operation === "update") &&
            (!assignmentId || !taskDetails.name)
        ) {
            setAlertMsgg({ ...alertMsgg, open: true, content: "Please select assignment and enter task name.", type: "error" });
            return;
        }

        // For delete, ensure task_id is present
        if ((operation === "delete" || operation === "update") && !taskDetails["id"]) {
            setAlertMsgg({ ...alertMsgg, open: true, content: "No task selected for deletion.", type: "error" });
            return;
        }

        // Prepare payload
        let payload = {};
        if (operation === "create") {
            payload = {
                assignment: assignmentId,
                name: taskDetails.name,
                description: taskDetails.description || "",
                status: "pending"
            };
        } else if (operation === "update") {
            payload = {
                task_id: taskDetails.id,
                assignment: assignmentId,
                name: taskDetails.name,
                description: taskDetails.description || "",
                status: taskDetails.status || "pending"
            };
        } else if (operation === "delete") {
            payload = {
                task_id: taskDetails.id,
                todelete: true
            };
        }

        try {
            setshowloader(true);
            const response = await axios.post(
                "/assignments/manageTasks/",
                payload,
                { withCredentials: true }
            );
            if (response && (response.status === "CREATED" || response.status === "UPDATED" || response.status === "DELETED")) {
                setAlertMsgg({ ...alertMsgg, open: true, content: `Task ${operation}d successfully.`, type: "success" });
                // Refresh the tasks list from parent
                if (onTasksUpdate) {
                    onTasksUpdate();
                }
            } else {
                setAlertMsgg({ ...alertMsgg, open: true, content: `Failed to ${operation} task.`, type: "error" });
            }
            setshowloader(false);
        } catch (error: any) {
            console.log(error);

            if (error.response) {
                setAlertMsgg({ ...alertMsgg, open: true, content: error.response.data?.error || "Error occurred", type: "error" });
            } else {
                setAlertMsgg({ ...alertMsgg, open: true, content: "Network error", type: "error" });
            }
            setshowloader(false);
        }
    };

    const handleSteps = async (operation = "create") => {
        // For create/update, ensure required fields are present
        if (
            (operation === "create" || operation === "update") &&
            (!stepDetails.title || !stepDetails.task)
        ) {
            setAlertMsgg({ ...alertMsgg, open: true, content: "Please select task and enter step title.", type: "error" });
            return;
        }

        // For delete, ensure step_id is present
        if ((operation === "delete" || operation === "update") && !stepDetails["id"]) {
            setAlertMsgg({ ...alertMsgg, open: true, content: "No step selected for deletion.", type: "error" });
            return;
        }

        // Prepare payload
        let payload = {};
        if (operation === "create") {
            payload = {
                task: Number(stepDetails.task),
                title: stepDetails.title,
                description: stepDetails.description || "",
                status: stepDetails.status || "pending",
                can_assignee_view: true,
                can_client_view: stepDetails.can_client_view,
                order: stepDetails.order ?? 0
            };
        } else if (operation === "update") {
            payload = {
                step_id: stepDetails.id,
                task: Number(stepDetails.task),
                title: stepDetails.title,
                description: stepDetails.description || "",
                status: stepDetails.status || "pending",
                can_assignee_view: true,
                can_client_view: stepDetails.can_client_view,
                order: stepDetails.order ?? 0
            };
        } else if (operation === "delete") {
            payload = {
                step_id: stepDetails.id,
                todelete: true
            };
        }

        try {
            setshowloader(true);
            const response = await axios.post(
                "/assignments/manageSteps/",
                payload,
                { withCredentials: true }
            );
            if (response && (response.status === "CREATED" || response.status === "UPDATED" || response.status === "DELETED")) {
                setAlertMsgg({ ...alertMsgg, open: true, content: `Step ${operation}d successfully.`, type: "success" });
                fetch_task();
            } else {
                setAlertMsgg({ ...alertMsgg, open: true, content: `Failed to ${operation} step.`, type: "error" });
            }
            setshowloader(false);
        } catch (error: any) {
            if (error.response) {
                setAlertMsgg({ ...alertMsgg, open: true, content: error.response.data?.error || "Error occurred", type: "error" });
            } else {
                setAlertMsgg({ ...alertMsgg, open: true, content: "Network error", type: "error" });
            }
            setshowloader(false);
        }
    };

    useEffect(() => {
        if (taskId > 0) {
            fetch_task();
        } else {
            setshowloader(false);
        }
    }, []);

    useEffect(() => {
        if (taskId > 0) {
            setshowloader(true);
            fetch_task();
        } else {
            setshowloader(false);
        }
    }, [taskId]);

    document.title = "Task | BizServe";

    return (
        <React.Fragment>
            {alertMsgg.open && (
                <Alert 
                    color={alertMsgg.type === "success" ? "success" : "danger"} 
                    toggle={handleAlertMsggClose}
                    style={{ fontWeight: "bold" }}
                >
                    {alertMsgg.content}
                </Alert>
            )}
            <div className="loaderContainer" hidden={!showloader}>
                <div className="spinner">
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                </div>
            </div>
            {canEdit && (
                <div className="mb-3 d-flex justify-content-end" hidden={showloader}>
                    <Button
                        color={editMode ? "success" : "secondary"}
                        size="sm"
                        onClick={() => setEditMode(!editMode)}
                    >
                        <i className={`bx ${editMode ? "bx-check" : "bx-edit"} font-size-14 align-middle me-1`}></i>
                        {editMode ? "Exit Edit Mode" : "Edit Mode"}
                    </Button>
                </div>
            )}
            <Row style={{
                marginRight: "0px",
                display: showloader ? "none" : "flex"
            }}>
                <Col xl="3">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="mb-0">Tasks</h5>
                        {canEdit && editMode && (
                            <Button
                                color="primary"
                                size="sm"
                                onClick={() => {
                                    setTaskDetails({
                                        name: "",
                                        description: ""
                                    });
                                    toggleAddUpdateTaskModal();
                                }}
                            >
                                <i className="bx bx-plus font-size-14 align-middle"></i>
                            </Button>
                        )}
                    </div>
                    <Nav pills className="flex-column">
                        {tasksData && tasksData.length > 0 ? (
                            tasksData.map((task, index) => {
                                return (<NavItem key={index}>
                                    <div className="d-flex align-items-center mb-2">
                                        <NavLink
                                            style={{ cursor: "pointer", flex: 1 }}
                                            className={classnames({
                                                "mb-0": true,
                                                active: task.id === taskId,
                                            })}
                                            onClick={() => {
                                                setTaskId(task.id);
                                            }}
                                        >
                                            {task.name}
                                        </NavLink>
                                        {canEdit && editMode && (
                                            <ActionMenu
                                                actions={[
                                                    {
                                                        icon: "bx bx-pencil",
                                                        label: "Edit",
                                                        onClick: () => {
                                                            setTaskDetails({ ...task });
                                                            toggleAddUpdateTaskModal();
                                                        },
                                                        href: "#",
                                                        disabled: false
                                                    },
                                                    {
                                                        icon: "bx bx-trash text-danger",
                                                        label: "Delete",
                                                        onClick: () => {
                                                            setTaskDetails({ id: task.id });
                                                            toggleDeleteTaskModal();
                                                        },
                                                        href: "#",
                                                        disabled: false
                                                    }
                                                ]}
                                            />
                                        )}
                                    </div>
                                </NavItem>)
                            })
                        ) : (
                            <div className="text-muted text-center py-4">
                                <p>No tasks available.</p>
                                {canEdit && editMode && (
                                    <p className="small">Click the + button above to create a task.</p>
                                )}
                            </div>
                        )}
                    </Nav>
                </Col>

                <Col xl="4">
                    <Card>
                        <CardBody>
                            {taskId > 0 ? (
                                <>
                                    <div className="d-flex justify-content-between align-items-center mb-4">
                                        <h4 className="card-title mb-0">Steps</h4>
                                        {canEdit && editMode && (
                                            <Button
                                                color="primary"
                                                size="sm"
                                                onClick={() => {
                                                    setStepDetails({
                                                        title: "",
                                                        description: "",
                                                        status: "pending",
                                                        task: taskId,
                                                        can_assignee_view: true,
                                                        can_client_view: true,
                                                        order: (stepsData || []).length
                                                    });
                                                    toggleAddUpdateStepModal();
                                                }}
                                            >
                                                <i className="bx bx-plus font-size-14 align-middle"></i>
                                            </Button>
                                        )}
                                    </div>

                                    <div>
                                        <ul className="verti-timeline list-unstyled">
                                            {
                                                (stepsData || []).map((step: Record<string, any>, index: number) => (
                                                    <li className="event-list" key={index}>
                                                        <div className="event-timeline-dot">
                                                            <i className={`${step.status === "completed" ? "fas fa-check-circle text-success" : "bx bx-right-arrow-circle"}`} />
                                                        </div>
                                                        <div className="d-flex">
                                                            <div className="flex-grow-1">
                                                                <div>
                                                                    <h5 className="font-size-14 text-primary">
                                                                        {step.title || <span className="text-muted">Untitled Step</span>}
                                                                        {userRole !== 'cst' && step.can_client_view && (
                                                                            <span className="badge badge-soft-primary m-2 p-1 text-dark">Client</span>
                                                                        )}
                                                                    </h5>
                                                                    <p className="text-muted">{step.description}</p>
                                                                </div>
                                                            </div>
                                                            <div className="flex-shrink-0 me-2">
                                                                {(step.status != "completed" || (canEdit && editMode)) && (
                                                                    <ActionMenu
                                                                        actions={[
                                                                            ...(step.status != "completed" ? [{ icon: "fas fa-check text-success", label: "Complete", onClick: () => { changeStepStatus(step.id, "completed") }, href: "#", disabled: false }] : []),
                                                                            ...(canEdit && editMode ? [
                                                                                { icon: "bx bx-pencil", label: "Edit", onClick: () => { setStepDetails({ ...step }); toggleAddUpdateStepModal(); }, href: "#", disabled: false },
                                                                                { icon: "bx bx-trash text-danger", label: "Delete", onClick: () => { setStepDetails({ id: step.id }); toggleDeleteStepModal(); }, href: "#", disabled: false }
                                                                            ] : [])
                                                                        ]}
                                                                    />
                                                                )}
                                                            </div>
                                                        </div>
                                                    </li>
                                                ))
                                            }
                                        </ul>
                                    </div>
                                </>
                            ) : (
                                <div className="text-muted text-center py-4">
                                    <h4 className="card-title mb-3">Steps</h4>
                                    <p>No task selected.</p>
                                </div>
                            )}
                        </CardBody>
                    </Card>
                </Col>

                <Col xl="5">
                    <Card>
                        <CardBody>
                            {taskId > 0 ? (
                                <>
                                    <h4 className="card-title mb-4">Files</h4>
                                    <div className="mb-3 d-flex align-items-center gap-2">
                                        <input type="file" onChange={(e) => setSelectedFile(e.target.files?.[0] || null)} />
                                        <button className="btn btn-primary" onClick={handleUpload}>Upload</button>
                                    </div>
                                    <div>
                                        <ul className="list-unstyled">
                                            {(filesData as any[]).map((f: any, idx: number) => {
                                                const key = f.fileName || f.location || f.key;
                                                const name = f.name || key?.split("/").pop();
                                                console.log('Rendering file:', { key, name, file: f });
                                                return (
                                                    <li key={idx} className="d-flex justify-content-between align-items-center mb-2">
                                                        <span title={key} className="flex-grow-1 me-2">{name}</span>
                                                        <div className="d-flex align-items-center">
                                                            <i 
                                                                className="fas fa-eye text-primary me-2" 
                                                                style={{ cursor: 'pointer', fontSize: '16px' }}
                                                                title="View file"
                                                                onClick={async () => {
                                                                    console.log('View clicked for:', key);
                                                                    const url = await get_view_url(key);
                                                                    if (url) {
                                                                        console.log('Opening URL:', url);
                                                                        window.open(url, "_blank");
                                                                    } else {
                                                                        setAlertMsgg({ ...alertMsgg, open: true, content: "Could not generate view URL.", type: "error" });
                                                                    }
                                                                }}
                                                            ></i>
                                                            <ActionMenu
                                                                actions={[
                                                                    {
                                                                        icon: "fas fa-download text-success",
                                                                        label: "Download",
                                                                        onClick: async () => {
                                                                            console.log('Download clicked for:', key);
                                                                            const url = await get_view_url(key);
                                                                            if (url) {
                                                                                console.log('Downloading from URL:', url);
                                                                                window.location.href = url;
                                                                            } else {
                                                                                setAlertMsgg({ ...alertMsgg, open: true, content: "Could not generate download URL.", type: "error" });
                                                                            }
                                                                        },
                                                                        href: "#",
                                                                        disabled: false
                                                                    },
                                                                    ...(canEdit && editMode ? [{
                                                                        icon: "fas fa-trash text-danger",
                                                                        label: "Delete",
                                                                        onClick: () => {
                                                                            console.log('Delete clicked for:', key);
                                                                            deleteFile(key);
                                                                        },
                                                                        href: "#",
                                                                        disabled: false
                                                                    }] : [])
                                                                ]}
                                                            />
                                                        </div>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                        {filesData.length === 0 && (
                                            <p className="text-muted">No files uploaded yet.</p>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <div className="text-muted text-center py-4">
                                    <h4 className="card-title mb-3">Files</h4>
                                    <p>No task selected.</p>
                                </div>
                            )}
                        </CardBody>
                    </Card>
                    <Card>
                        <CardBody>
                            {taskId > 0 ? (
                                <CommentsBox comments={commentsData as Comment[]} loading={false} addNewComment={addNewComment} />
                            ) : (
                                <div className="text-muted text-center py-4">
                                    <h4 className="card-title mb-3">Comments</h4>
                                    <p>No task selected.</p>
                                </div>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Add/Update Task */}
            <Modal
                isOpen={addTaskModal}
                size="lg"
                toggle={toggleAddUpdateTaskModal}
                backdrop={'static'}
                centered
            >
                <ModalHeader toggle={toggleAddUpdateTaskModal}>
                    <div className="modal-title mt-0 font-size-16 text-dark">
                        {taskDetails && taskDetails.id ? "Edit Task" : "Add Task"}
                    </div>
                </ModalHeader>
                <div className="modal-body">
                    <form>
                        <Row>
                            <Col lg={12}>
                                <div className="mb-3">
                                    <label className="form-label">Task Name</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={taskDetails.name}
                                        onChange={e =>
                                            setTaskDetails({ ...taskDetails, name: e.target.value })
                                        }
                                        placeholder="Enter task name"
                                    />
                                </div>
                            </Col>
                            <Col lg={12}>
                                <div className="mb-3">
                                    <label className="form-label">Description</label>
                                    <textarea
                                        className="form-control"
                                        value={taskDetails.description}
                                        onChange={e =>
                                            setTaskDetails({ ...taskDetails, description: e.target.value })
                                        }
                                        placeholder="Enter description"
                                    />
                                </div>
                            </Col>
                            <Col lg={12}>
                                <div className="d-flex justify-content-end">
                                    <button
                                        className="btn btn-primary mb-3"
                                        type="button"
                                        onClick={() => {
                                            if (taskDetails && taskDetails.id) {
                                                handleTask("update");
                                            } else {
                                                handleTask("create");
                                            }
                                            toggleAddUpdateTaskModal();
                                        }}
                                    >
                                        {taskDetails && taskDetails.id ? "Update Task" : "Add Task"}
                                    </button>
                                </div>
                            </Col>
                        </Row>
                    </form>
                </div>
            </Modal>

            {/* Delete Task */}
            <Modal
                isOpen={deleteTaskModal}
                toggle={() => {
                    toggleDeleteTaskModal();
                }}
                centered
                className="rounded-4"
            >
                <ModalHeader className="py-3 px-4" toggle={() => { toggleDeleteTaskModal(); }}>
                    <div className="modal-title mt-0 font-size-16 text-dark">
                        Delete Task
                    </div>
                </ModalHeader>

                <div className="modal-body">
                    <p>Are you sure you want to delete this task?</p>
                </div>

                <div className="modal-footer">
                    <button
                        type="button"
                        className="btn btn-sm btn-soft-dark"
                        onClick={() => { toggleDeleteTaskModal(); }}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={() => {
                            handleTask("delete");
                            toggleDeleteTaskModal();
                        }}
                    >
                        Delete
                    </button>
                </div>
            </Modal>

            {/* Add/Update Step */}
            <Modal
                isOpen={addStepModal}
                size="lg"
                toggle={toggleAddUpdateStepModal}
                backdrop={'static'}
                centered
            >
                <ModalHeader toggle={toggleAddUpdateStepModal}>
                    <div className="modal-title mt-0 font-size-16 text-dark">
                        {stepDetails && stepDetails.id ? "Edit Step" : "Add Step"}
                    </div>
                </ModalHeader>
                <div className="modal-body">
                    <form>
                        <Row>
                            <Col lg={12}>
                                <div className="mb-3">
                                    <label className="form-label">Step Title</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={stepDetails.title}
                                        onChange={e =>
                                            setStepDetails({ ...stepDetails, title: e.target.value })
                                        }
                                        placeholder="Enter step title"
                                    />
                                </div>
                            </Col>
                            <Col lg={12}>
                                <div className="mb-3">
                                    <label className="form-label">Description</label>
                                    <textarea
                                        className="form-control"
                                        value={stepDetails.description}
                                        onChange={e =>
                                            setStepDetails({ ...stepDetails, description: e.target.value })
                                        }
                                        placeholder="Enter description"
                                    />
                                </div>
                            </Col>
                            <Col lg={6}>
                                <div className="mb-3">
                                    <label className="form-label d-block">Status</label>
                                    <div className="form-check form-switch form-switch-md">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            id="stepStatus"
                                            checked={stepDetails.status === "completed"}
                                            onChange={e =>
                                                setStepDetails({ ...stepDetails, status: e.target.checked ? "completed" : "pending" })
                                            }
                                        />
                                        <label className={`form-check-label ${stepDetails.status === "completed" ? "text-success" : "text-dark"}`} htmlFor="stepStatus">
                                            {stepDetails.status === "completed" ? "Completed" : "Pending"}
                                        </label>
                                    </div>
                                </div>
                            </Col>
                            <Col lg={6}>
                                <div className="mb-3">
                                    <label className="form-label d-block">Task for Client?</label>
                                    <div className="form-check form-switch form-switch-md">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            id="canClientView"
                                            checked={stepDetails.can_client_view}
                                            onChange={e =>
                                                setStepDetails({ ...stepDetails, can_client_view: e.target.checked })
                                            }
                                        />
                                        <label className="form-check-label" htmlFor="canClientView">
                                            {stepDetails.can_client_view ? "Yes" : "No"}
                                        </label>
                                    </div>
                                </div>
                            </Col>
                            <Col lg={12}>
                                <div className="d-flex justify-content-end">
                                    <button
                                        className="btn btn-primary mb-3"
                                        type="button"
                                        onClick={() => {
                                            if (stepDetails && stepDetails.id) {
                                                handleSteps("update");
                                            } else {
                                                handleSteps("create");
                                            }
                                            toggleAddUpdateStepModal();
                                        }}
                                    >
                                        {stepDetails && stepDetails.id ? "Update Step" : "Add Step"}
                                    </button>
                                </div>
                            </Col>
                        </Row>
                    </form>
                </div>
            </Modal>

            {/* Delete Step */}
            <Modal
                isOpen={deleteStepModal}
                toggle={() => {
                    toggleDeleteStepModal();
                }}
                centered
                className="rounded-4"
            >
                <ModalHeader className="py-3 px-4" toggle={() => { toggleDeleteStepModal(); }}>
                    <div className="modal-title mt-0 font-size-16 text-dark">
                        Delete Step
                    </div>
                </ModalHeader>

                <div className="modal-body">
                    <p>Are you sure you want to delete this step?</p>
                </div>

                <div className="modal-footer">
                    <button
                        type="button"
                        className="btn btn-sm btn-soft-dark"
                        onClick={() => { toggleDeleteStepModal(); }}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={() => {
                            handleSteps("delete");
                            toggleDeleteStepModal();
                        }}
                    >
                        Delete
                    </button>
                </div>
            </Modal>
        </React.Fragment>
    )
};

export default PETaskManagement;
